import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Eye, EyeOff, Mail, Phone, User, Lock, Ticket, Shield, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { GAM_TICKET_LOGO } from '@/config/assets';
import { STADIUM_BG } from '@/config/assets';

export default function UserRegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registerMethod, setRegisterMethod] = useState('email');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (registerMethod === 'email' && !email) {
      toast.error('Please enter your email address');
      return;
    }

    if (registerMethod === 'phone' && !phone) {
      toast.error('Please enter your phone number');
      return;
    }
    
    setLoading(true);
    
    try {
      const payload = {
        name,
        password,
        ...(registerMethod === 'email' ? { email } : { phone })
      };

      const response = await axios.post(`${API_URL}/api/user/register`, payload);
      const { token, user } = response.data;
      
      localStorage.setItem('user_token', token);
      localStorage.setItem('user_data', JSON.stringify(user));
      
      toast.success('Account created successfully!');
      window.location.href = '/my-tickets';
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${STADIUM_BG})` }}
      >
        <div className="absolute inset-0 bg-black/60" />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header className="p-6">
          <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white/30 shadow-xl mx-auto">
            <img src={GAM_TICKET_LOGO} alt="Gam Ticket" className="w-full h-full object-cover" />
          </div>
        </header>

        {/* Main Content - Side by Side */}
        <main className="flex-1 flex items-center justify-center px-6 pb-20">
          <div className="w-full max-w-5xl flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
            
            {/* Left Side - Branding */}
            <div className="flex-1 text-white text-center lg:text-left">
              <h1 className="text-4xl lg:text-5xl font-bold mb-4 leading-tight">
                Join The<br />
                <span className="text-[#0055FF]">Gam Ticket</span><br />
                Community
              </h1>
              <p className="text-lg text-white/80 mb-8 max-w-md mx-auto lg:mx-0">
                Create your account and never miss a match or event again!
              </p>
              
              {/* Features - Horizontal on Desktop */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur flex items-center justify-center">
                    <Ticket className="w-5 h-5 text-[#0055FF]" />
                  </div>
                  <span className="text-sm font-medium">Digital Tickets</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur flex items-center justify-center">
                    <Shield className="w-5 h-5 text-green-500" />
                  </div>
                  <span className="text-sm font-medium">Secure & Safe</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-red-500" />
                  </div>
                  <span className="text-sm font-medium">Notifications</span>
                </div>
              </div>
            </div>

            {/* Right Side - Registration Form */}
            <div className="w-full max-w-md">
              <Card className="bg-white/95 backdrop-blur-xl shadow-2xl border-0 rounded-2xl overflow-hidden">
                <CardHeader className="text-center pb-2 pt-6">
                  <CardTitle className="text-2xl font-bold text-slate-800">Create Account</CardTitle>
                  <p className="text-slate-500 text-sm mt-1">
                    Register to save your tickets
                  </p>
                </CardHeader>

                <CardContent className="pb-6">
                  {/* Registration Method Toggle */}
                  <div className="flex gap-2 p-1 bg-slate-100 rounded-xl mb-5">
                    <button
                      type="button"
                      onClick={() => setRegisterMethod('email')}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                        registerMethod === 'email' 
                          ? 'bg-white shadow-sm text-slate-800' 
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      <Mail className="w-4 h-4" />
                      Email
                    </button>
                    <button
                      type="button"
                      onClick={() => setRegisterMethod('phone')}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                        registerMethod === 'phone' 
                          ? 'bg-white shadow-sm text-slate-800' 
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      <Phone className="w-4 h-4" />
                      Phone
                    </button>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-3">
                    <div>
                      <Label className="text-sm font-medium mb-1.5 block text-slate-700">
                        Full Name
                      </Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Your full name"
                          required
                          className="pl-10 h-11 rounded-xl border-slate-200 focus:border-[#0055FF] focus:ring-[#0055FF]/20"
                          data-testid="user-register-name"
                        />
                      </div>
                    </div>

                    {registerMethod === 'email' ? (
                      <div>
                        <Label className="text-sm font-medium mb-1.5 block text-slate-700">
                          Email Address
                        </Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="your@email.com"
                            required={registerMethod === 'email'}
                            className="pl-10 h-11 rounded-xl border-slate-200 focus:border-[#0055FF] focus:ring-[#0055FF]/20"
                            data-testid="user-register-email"
                          />
                        </div>
                      </div>
                    ) : (
                      <div>
                        <Label className="text-sm font-medium mb-1.5 block text-slate-700">
                          Phone Number
                        </Label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="+220 XXX XXXX"
                            required={registerMethod === 'phone'}
                            className="pl-10 h-11 rounded-xl border-slate-200 focus:border-[#0055FF] focus:ring-[#0055FF]/20"
                            data-testid="user-register-phone"
                          />
                        </div>
                      </div>
                    )}

                    <div>
                      <Label className="text-sm font-medium mb-1.5 block text-slate-700">
                        Password
                      </Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="At least 6 characters"
                          required
                          className="pl-10 pr-10 h-11 rounded-xl border-slate-200 focus:border-[#0055FF] focus:ring-[#0055FF]/20"
                          data-testid="user-register-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <Label className="text-sm font-medium mb-1.5 block text-slate-700">
                        Confirm Password
                      </Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Confirm your password"
                          required
                          className="pl-10 h-11 rounded-xl border-slate-200 focus:border-[#0055FF] focus:ring-[#0055FF]/20"
                          data-testid="user-register-confirm-password"
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full h-11 rounded-xl bg-[#0055FF] hover:bg-[#0044CC] transition-all duration-300 font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 mt-2"
                      data-testid="user-register-submit"
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        'Create Account'
                      )}
                    </Button>
                  </form>

                  <div className="mt-5 text-center">
                    <p className="text-slate-500 text-sm">
                      Already have an account?{' '}
                      <Link to="/" className="text-[#0055FF] hover:underline font-semibold">
                        Sign In
                      </Link>
                    </p>
                  </div>

                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <Link 
                      to="/events" 
                      className="flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-[#0055FF] transition-colors"
                    >
                      <Calendar className="w-4 h-4" />
                      Browse Events
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="p-4 text-center">
          <p className="text-white/50 text-sm">
            © 2026 Gam Ticket. The Premier Ticketing Platform for The Gambia.
          </p>
        </footer>
      </div>
    </div>
  );
}
